import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { PackageManagementInjection } from "features/packageManagement/di/PackageManagementInjection";
import { PackageActionCommandEntity } from "features/packageManagement/domain/entities/PackageActionEntity";
import { PackageLookupRequestEntity } from "features/packageManagement/domain/entities/PackageLookupEntity";
import { PackageListRequestEntity } from "features/packageManagement/domain/entities/PackageListRequestEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { IPackageManagementObservedState } from "../interfaces/IPackageManagement";

class PackageManagementInput implements Disposable {
  public fetchPackageList = new BehaviorSubject<PackageListRequestEntity>({});
  public fetchCountry = new BehaviorSubject<PackageLookupRequestEntity>({});
  public fetchSupplierList =
    new BehaviorSubject<PackageLookupRequestEntity>({});
  public fetchCarrier = new BehaviorSubject<PackageLookupRequestEntity>({});
  public fetchRoamingPartnerList =
    new BehaviorSubject<PackageLookupRequestEntity>({});
  public fetchDetail = new BehaviorSubject<string>("");
  public fetchCategory = new BehaviorSubject<PackageLookupRequestEntity>({});
  public fetchActions = new BehaviorSubject<PackageActionCommandEntity>({});
  public resetDetail = new BehaviorSubject<void>(undefined);

  [Symbol.dispose](): void {
    this.fetchPackageList.complete();
    this.fetchCountry.complete();
    this.fetchSupplierList.complete();
    this.fetchCarrier.complete();
    this.fetchRoamingPartnerList.complete();
    this.fetchActions.complete();
    this.fetchDetail.complete();
    this.fetchCategory.complete();
    this.resetDetail.complete();
  }
}

class PackageManagementOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");
  public onActionError = new BehaviorSubject<{
    action?: "create" | "update";
    message?: string;
  }>({});
  public onActionSuccess = new BehaviorSubject<"create" | "update" | "">("");
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onActionError.complete();
    this.onActionSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

@injectable()
export class PackageManagementViewModel extends BasedObservedViewModel<
  PackageManagementInput,
  PackageManagementOutput,
  IPackageManagementObservedState
> {
  private useCase = PackageManagementInjection.getUseCase();

  constructor() {
    super(new PackageManagementInput(), new PackageManagementOutput());
  }

  protected override createObservedState(): IPackageManagementObservedState {
    return {
      loading: false,
      modalLoading: false,
      list: [],
      paging: { page: 0, limit: 10, total: 0 },
      detailInfo: {},
      categoryList: [],
      carrierList: [],
      countryList: [],
      supplierList: [],
      roamingPartnerList: [],
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(
      this.input.fetchPackageList,
      async (params, guard) => {
        try {
          this.setObservedState({ loading: true });
          const res = await this.useCase.getPackageManagementList(params);
          guard?.runIfLatest(() => {
            this.setObservedState({
              list: res?.data ?? [],
              paging: res?.pagination ?? { page: 0, limit: 10, total: 0 },
            });
          });
        } catch (error) {
          console.error(error);
          guard?.runIfLatest(() => {
            this.setObservedState({
              list: [],
              paging: { page: 0, limit: 10, total: 0 },
            });
          });
          this.output.onError.next(
            error?.message || strings.GlobalMessage.Error,
          );
        } finally {
          guard?.runIfLatest(() => this.setObservedState({ loading: false }));
        }
      },
    );

    this.skipFirstInitial(this.input.fetchDetail, async (id) => {
      if (!id) return;
      try {
        this.setObservedState({ modalLoading: true });
        const res = await this.useCase.getPackageManagementDetail(id);
        if (res?.data) {
          this.setObservedState({
            detailInfo: {
              ...res.data,
              more: Array.isArray(res.data.more)
                ? res.data.more.map((item) => ({
                    ...item,
                    tag: item.name ? "section" : "tag",
                    name: item.name ?? "",
                  }))
                : [],
            },
          });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.setObservedState({ modalLoading: false });
      }
    });

    this.skipFirstInitial(this.input.fetchCategory, async (params) => {
      try {
        const categoryList = await this.useCase.getPackageCategories(params);
        this.setObservedState({ categoryList });
      } catch (error) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      }
    });

    this.skipFirstInitial(this.input.fetchCountry, async (params) => {
      try {
        const countryList = await this.useCase.getPackageCountries(params);
        this.setObservedState({ countryList });
      } catch (error) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      }
    });

    this.skipFirstInitial(this.input.fetchSupplierList, async (params) => {
      try {
        const supplierList = await this.useCase.getPackageSuppliers(params);
        this.setObservedState({ supplierList });
      } catch (error) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      }
    });

    this.skipFirstInitial(
      this.input.fetchRoamingPartnerList,
      async (params) => {
        try {
          const roamingPartnerList =
            await this.useCase.getPackageRoamingPartners(params);
          this.setObservedState({ roamingPartnerList });
        } catch (error) {
          console.error(error);
          this.output.onError.next(
            error?.message || strings.GlobalMessage.Error,
          );
        }
      },
    );

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const carrierList = await this.useCase.getPackageCarriers(params);
        this.setObservedState({ carrierList });
      } catch (error) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error,
        );
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (command) => {
      if (!command.action || !command.body || !command.service) return;
      try {
        this.setObservedState({ modalLoading: true });
        const res = await this.useCase.savePackageManagement(command);
        if (res?.data) {
          this.output.onActionSuccess.next(command.action);
        } else {
          this.output.onActionError.next({
            action: command.action,
            message: res?.message || strings.GlobalMessage.Error,
          });
        }
      } catch (error) {
        console.error(error);
        this.output.onActionError.next({
          action: command.action,
          message: error?.message || strings.GlobalMessage.Error,
        });
      } finally {
        this.setObservedState({ modalLoading: false });
      }
    });

    this.skipFirstInitial(this.input.resetDetail, () => {
      this.setObservedState({ detailInfo: {} });
    });
  }
}
